import { defineStore } from "pinia"
import { useDashboardApi } from "../composables/useDashboardApi"
import type { DashboardAuthSession } from "../composables/useDashboardApi"
import { dashboardEnvironment } from "../utils/env"

const storageKey = "undetect.dashboard.auth"
const fixtureTokenTtlMillis = 30 * 24 * 60 * 60 * 1000

interface AuthSnapshot {
  authenticatedAt: number
  dashboardToken: string
  dashboardTokenExpiresAt: number
}

function readSnapshot(): AuthSnapshot | null {
  if (typeof window === "undefined") {
    return null
  }

  try {
    const raw = window.localStorage.getItem(storageKey)
    return raw ? (JSON.parse(raw) as AuthSnapshot) : null
  } catch {
    return null
  }
}

function writeSnapshot(snapshot: AuthSnapshot | null) {
  if (typeof window === "undefined") {
    return
  }

  if (!snapshot) {
    window.localStorage.removeItem(storageKey)
    return
  }

  window.localStorage.setItem(storageKey, JSON.stringify(snapshot))
}

function isUsableSnapshot(snapshot: AuthSnapshot | null, now = Date.now()): snapshot is AuthSnapshot {
  return Boolean(snapshot?.dashboardToken && snapshot.dashboardTokenExpiresAt > now)
}

function fixtureSession(now = Date.now()): DashboardAuthSession {
  return {
    expiresAt: now + fixtureTokenTtlMillis,
    issuedAt: now,
    token: `fixture-dashboard-token-${now}`,
    tokenType: "Bearer"
  }
}

export const useAuthStore = defineStore("auth", {
  actions: {
    async login(dashboardPassword: string) {
      const trimmedPassword = dashboardPassword.trim()

      if (!trimmedPassword) {
        return false
      }

      const session =
        dashboardEnvironment.dataMode === "fixtures"
          ? fixtureSession()
          : await useDashboardApi().createDashboardSession(trimmedPassword)

      this.authenticatedAt = session.issuedAt
      this.dashboardToken = session.token
      this.dashboardTokenExpiresAt = session.expiresAt
      this.restored = true

      writeSnapshot({
        authenticatedAt: this.authenticatedAt,
        dashboardToken: this.dashboardToken,
        dashboardTokenExpiresAt: this.dashboardTokenExpiresAt
      })

      return true
    },
    logout() {
      this.authenticatedAt = 0
      this.dashboardToken = ""
      this.dashboardTokenExpiresAt = 0
      this.restored = true
      writeSnapshot(null)
    },
    restore() {
      if (this.restored) {
        return
      }

      const snapshot = readSnapshot()
      this.restored = true

      if (!isUsableSnapshot(snapshot)) {
        writeSnapshot(null)
        return
      }

      this.authenticatedAt = snapshot.authenticatedAt
      this.dashboardToken = snapshot.dashboardToken
      this.dashboardTokenExpiresAt = snapshot.dashboardTokenExpiresAt
    }
  },
  getters: {
    isAuthenticated: (state) =>
      state.authenticatedAt > 0 && state.dashboardToken.length > 0 && state.dashboardTokenExpiresAt > Date.now(),
    sessionLabel: (state) => {
      if (!state.dashboardToken || state.dashboardTokenExpiresAt <= Date.now()) {
        return "not signed in"
      }

      return "signed in"
    },
    sessionExpiryLabel: (state) => {
      if (!state.dashboardToken || state.dashboardTokenExpiresAt <= Date.now()) {
        return "Token inactive"
      }

      const expiresAt = new Date(state.dashboardTokenExpiresAt)
      return Number.isNaN(expiresAt.getTime()) ? "Token active" : `Token expires ${expiresAt.toLocaleString()}`
    }
  },
  state: () => ({
    authenticatedAt: 0,
    dashboardToken: "",
    dashboardTokenExpiresAt: 0,
    restored: false
  })
})
